import React from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { Stagger, StaggerItem } from "../lib/motion";

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= rating ? "fill-gold text-gold" : "text-rule"}`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

/**
 * Reviews are only ever rendered from collected, verified purchases.
 * With nothing collected yet the section states that plainly rather than
 * showing sample copy — a fabricated review is worse than an empty shelf.
 */
export function Testimonials({ items = [] }) {
  if (!items.length) {
    return (
      <div className="card-s p-8 lg:p-12" data-testid="testimonials-empty">
        <p className="eyebrow">No reviews yet</p>
        <h3 className="mt-4 text-xl sm:text-2xl">
          This space stays empty until real customers fill it
        </h3>
        <p className="ui measure mt-5 text-sm leading-[1.7] text-dim">
          We have not launched to enough people to have collected reviews we can stand behind, and we
          are not going to write our own. When reviews appear here they will come from verified
          purchases only, we will not edit them, we will not remove the unflattering ones, and we will
          never have paid for one. Until then, judge us on the things you can check yourself: the
          laboratory reports, the published X-rays and the numbers on our scoreboard.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-5">
          <Link to="/tools/verify-a-certificate" className="btn btn-secondary" data-testid="testimonials-verify-cta">
            Verify any certificate
          </Link>
          <Link to="/about/scoreboard" className="link-u ui text-sm" data-testid="testimonials-scoreboard-cta">
            See our scoreboard →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <Stagger
      className="grid gap-[18px] sm:grid-cols-[repeat(auto-fill,minmax(310px,1fr))]"
      stagger={0.07}
      data-testid="testimonials"
    >
      {items.map((r) => (
        <StaggerItem key={`${r.author}-${r.date}`}>
          <article className="card-s elev relative flex h-full flex-col overflow-hidden p-7" data-testid="review-card">
            <span className="quote-glyph pointer-events-none absolute right-4 top-2" aria-hidden="true">
              &rdquo;
            </span>
            <Stars rating={r.rating} />
            <p className="mt-5 flex-1 text-base italic leading-[1.7] text-ink">{r.body}</p>
            <div className="mt-7 flex items-center gap-3 border-t border-rule pt-5">
              <span
                className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border border-rule text-sm text-gold"
                style={{ fontFamily: "'Fraunces', Georgia, serif" }}
                aria-hidden="true"
              >
                {(r.author || "?").trim().charAt(0).toUpperCase()}
              </span>
              <span>
                <span className="ui block text-xs font-medium text-ink">{r.author}</span>
                <span className="ui block text-[11px] text-dim">{r.location}</span>
              </span>
              <span className="ui ml-auto text-[10px] uppercase tracking-[0.12em] text-verify">
                Verified purchase
              </span>
            </div>
          </article>
        </StaggerItem>
      ))}
    </Stagger>
  );
}
